import { useState, useEffect } from "react";
import { createTheme } from "@material-ui/core/styles";
import {
  getColorScheme,
  getColorSchemeLight,
  isDarkEnabled,
  isInit,
} from "../Components/Common/Utility";

const createAppTheme = () => {
  return createTheme({
    typography: {
      fontFamily: "Montserrat",
      fontSize: 14,
    },
    palette: {
      background: {
        default: isDarkEnabled() ? "#2a2d38" : "#fafafa",
      },
    },
    borderColor: isDarkEnabled() ? "#9c9ba0" : "black",
    borderColorAlt: isDarkEnabled() ? "#a2aac3" : "black",
    buttonClickableBorder: getColorScheme(),
    buttonClickableBorderAlt: getColorSchemeLight(),
    buttonClickableText: isDarkEnabled() ? "#9c9ba0" : "inherit",
    buttonDisabled: isDarkEnabled() ? "#5d5e6a" : "black",
    ckEditorBackground: isDarkEnabled() ? "#383c4b" : "#fafafa",
    ckEditorButtonHover: isDarkEnabled() ? "#333644" : "inherit",
    colorScheme: getColorScheme(),
    colorSchemeLight: getColorSchemeLight(),
    companyName: getColorScheme(),
    loginBackground: isDarkEnabled() ? "#3F424C" : "#fafafa",
    dialogs: isDarkEnabled() ? "#333644" : "#EFEFEF",
    dialogsAlt: isDarkEnabled() ? "#6e7491" : "white",
    dividers: isDarkEnabled() ? "#a2aac3" : "white",
    dividersLight: isDarkEnabled() ? "#646369" : "#e6e6e6",
    icons: isDarkEnabled() ? "#c8cddc" : "inherit",
    inputFields: isDarkEnabled() ? "#383c4b" : "white",
    linkColor: getColorScheme(),
    linkColorFaded: getColorSchemeLight(),
    linkColorHover: getColorSchemeLight(),
    logoBackground: isDarkEnabled() ? "#f4f4f4" : "#fff",
    rightBackground: isDarkEnabled() ? "#f4f4f4" : "#f6f6f6",
    menuButton: isDarkEnabled() ? "white" : "grey",
    navBarOne: isDarkEnabled() ? "#2a2d38" : "", // Top navbar
    navBarTwo: isDarkEnabled() ? "#333644" : "#f6f6f6", // Second from top navbar (has links)
    navBarLink: isDarkEnabled() ? "#9c9ba0" : "#4b4a4f",
    notification: isDarkEnabled() ? "#383c4b" : "", // #ECDCDF
    paginationButton: isDarkEnabled() ? getColorScheme() : "inherit",
    sidebar: isDarkEnabled() ? "#35353c" : "#fff",
    tableHeaderBG: isDarkEnabled() ? "#2a2d38" : "",
    tableHeaderTitle: isDarkEnabled() ? "#9c9ba0" : "",
    tableRowHover: isDarkEnabled() ? "#383c4b" : "#e6e6e6",
    tableRowHoverAlt: isDarkEnabled() ? "#333644" : "#EEEEEE",
    primary: {
      "& :hover": {
        visibility: "hidden",
      },
    },
    subText: isDarkEnabled() ? "#9c9ba0" : "grey",
    tiText: "#585555",
    text: isDarkEnabled() ? "#c8cddc" : "black",
    textAlert: "#c43030",
    textOther: "#3f70b7",
    textSuccess: "#0acf97",
    textWarning: "#f7bc16",
    textThemed: getColorScheme(),

    // ----------- Theme overrides for Material-UI components ----------- //
    overrides: {
      MuiButton: {
        outlined: {
          color: isDarkEnabled() ? "#9c9ba0" : "grey",
          borderColor: getColorSchemeLight(),
          "&:hover": {
            backgroundColor: `${getColorScheme()} !important`,
          },
        },
      },

      MuiDivider: {
        root: {
          backgroundColor: isDarkEnabled() ? "#a2aac3" : "#c0c0c0",
        },
      },
      MuiIconButton: {
        root: {
          color: getColorSchemeLight(),
          "&:hover": {
            color: getColorScheme() + " !important",
          },
        },
      },
      MuiInputBase: {
        root: {
          borderColor: isDarkEnabled() ? "#a2aac3 !important" : "",
          "&:hover": {
            borderColor: isInit()
              ? "#e4e643 !important"
              : isDarkEnabled()
              ? getColorScheme()
              : "",
            "& .MuiInputAdornment-positionEnd .MuiIconButton-root": {
              color: `${getColorScheme()} !important`,
            },
            "& .MuiInputAdornment-positionEnd .MuiSvgIcon-root": {
              color: `${getColorScheme()} !important`,
            },
          },
          "&$focused": {
            "& > *": {
              borderColor: isInit()
                ? "#e4e643 !important"
                : getColorScheme() + " !important",
            },
            "& .MuiInputAdornment-positionEnd .MuiIconButton-root": {
              color: `${getColorScheme()} !important`,
            },
            "& .MuiInputAdornment-positionEnd .MuiSvgIcon-root": {
              color: `${getColorScheme()} !important`,
            },
          },
          "&:after": {
            borderColor: isInit()
              ? "#e4e643 !important"
              : `${getColorScheme()} !important`,
          },
          "&$disabled": {
            border: "none",
            "&:hover": {
              borderColor: "none !important",
            },
          },
        },
        input: {
          color: isDarkEnabled() ? "#9c9ba0" : "black",
        },
      },
      MuiInput: {
        root: {
          borderBottomColor: isDarkEnabled() ? "#9c9ba0" : "black",
        },
        underline: {
          "&:before": {
            borderBottom: "None !important",
          },
          "&:hover:before": {
            borderBottom: "None !important",
          },
        },
      },
      MuiInputAdornment: {
        positionEnd: {
          "& .MuiIconButton-root": {
            color: isDarkEnabled() ? getColorSchemeLight() : getColorScheme(),
            "&:hover": {
              color: getColorScheme() + " !important",
            },
          },
          "& .MuiSvgIcon-root": {
            color: isDarkEnabled() ? getColorSchemeLight() : getColorScheme(),
          },
        },
      },
      MuiInputLabel: {
        root: {
          "&$focused": {
            color: isInit()
              ? "grey !important"
              : `${getColorScheme()} !important`,
          },
        },
      },
      MuiFormLabel: {
        root: {
          color: isDarkEnabled() ? "#9c9ba0" : "grey",
        },
      },
      MuiFormHelperText: {
        root: {
          "&.Mui-error": {
            color: isDarkEnabled() ? "#c8cddc" : "#F44336",
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderColor: isDarkEnabled() ? getColorSchemeLight() : "#c0c0c0",
          "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDarkEnabled() ? getColorScheme() : "#999999",
            },
            "& .MuiInputAdornment-positionEnd .MuiIconButton-root": {
              color: `${getColorScheme()} !important`,
            },
            "& .MuiInputAdornment-positionEnd .MuiSvgIcon-root": {
              color: `${getColorScheme()} !important`,
            },
          },
          "&.Mui-focused": {
            "& .MuiInputAdornment-positionEnd .MuiIconButton-root": {
              color: `${getColorScheme()} !important`,
            },
            "& .MuiInputAdornment-positionEnd .MuiSvgIcon-root": {
              color: `${getColorScheme()} !important`,
            },
          },
          "&.Mui-error": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDarkEnabled() ? "red" : "#F44356",
            },
          },
        },
        notchedOutline: {
          borderColor: isDarkEnabled() ? "#a2aac3" : "",
        },
      },
      MuiListItem: {
        divider: {
          backgroundColor: "#c8cddc",
        },
      },
      MuiPaper: {
        root: {
          color: isDarkEnabled() ? "#9c9ba0" : "inherit",
          backgroundColor: isDarkEnabled() ? "#383c4b" : "#f4f4f4",
        },
      },
      MuiPickersDay: {
        day: {
          color: getColorScheme(),
        },
        daySelected: {
          backgroundColor: getColorScheme(),
        },
        current: {
          color: getColorScheme(),
        },
      },
      MuiPickersCalendarHeader: {
        daysHeader: {},
        dayLabel: {
          color: isDarkEnabled() ? "#9c9ba0" : "grey",
        },
        iconButton: {
          backgroundColor: "None !important",
        },
      },
      MuiPickersClock: {
        pin: {
          backgroundColor: getColorScheme(),
        },
      },
      MuiPickersClockPointer: {
        noPoint: {
          backgroundColor: getColorScheme(),
        },
        pointer: {
          backgroundColor: getColorScheme(),
        },
        thumb: {
          border: `14px solid ${getColorScheme()}`,
        },
      },
      MuiPickersToolbar: {
        toolbar: {
          backgroundColor: getColorScheme(),
        },
      },
      MuiRadio: {
        root: {
          color: getColorScheme(),
          colorPrimary: getColorScheme(),
          "&$checked": {
            color: getColorScheme(),
            colorPrimary: getColorScheme(),
            colorSecondary: getColorScheme(),
          },
          colorSecondary: getColorScheme(),
        },
        colorPrimary: {
          "&$checked": {
            color: getColorScheme(),
            colorPrimary: getColorScheme(),
            colorSecondary: getColorScheme(),
          },
        },
        colorSecondary: {
          "&$checked": {
            color: getColorScheme(),
            colorPrimary: getColorScheme(),
            colorSecondary: getColorScheme(),
          },
        },
      },
      MuiSelect: {
        icon: {
          color: getColorScheme(),
        },
      },
      MuiSlider: {
        root: {
          color: getColorScheme(),
          colorPrimary: getColorScheme(),
        },
        colorPrimary: {
          color: getColorScheme(),
        },
      },
      MuiStepIcon: {
        active: {
          color: `${getColorScheme()} !important`,
        },
        completed: {
          color: `${getColorScheme()} !important`,
        },
      },
      MuiTableCell: {
        root: {
          border: "None",
          borderBottom: "None",
        },
        head: {
          borderBottom: "1px solid rgb(162, 170, 195)",
        },
      },
    },
  });
};

export const useTheme = () => {
  const [theme, setTheme] = useState(() => createAppTheme());

  const updateTheme = () => {
    setTheme(createAppTheme());
    
    // Update Zendesk widget theme if available
    if (window.zESettings && window.zESettings.webWidget) {
      window.zESettings.webWidget["color"]["theme"] = getColorScheme();
      window.zESettings.webWidget["color"]["launcherText"] = "#fafafa";
    }
  };

  useEffect(() => {
    // Only listen for custom theme update events from specific user actions
    // (login, settings changes, etc.) - not broad localStorage changes
    const handleThemeUpdate = () => {
      updateTheme();
    };

    window.addEventListener('themeUpdate', handleThemeUpdate);

    return () => {
      window.removeEventListener('themeUpdate', handleThemeUpdate);
    };
  }, []);

  return { theme, updateTheme };
};